import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Reveal, Eyebrow } from "../../components/ui";
import { API_BASE_URL, ENDPOINTS } from "../../config/api";
import { formatMoney } from "../../utils/format";
import { getUser, authFetch, safeJson } from "../../utils/auth";
import {
  getMyRequest,
  acceptLead,
  declineLead,
  checkoutLead,
  verifyLeadPayment,
} from "../../services/leadService";
import { ApiError, type Lead, type MyRequestPayload } from "../../types/lead";
import { LeadHero } from "./components/LeadHero";
import { PaymentPicker } from "./components/PaymentPicker";
import { RequestForm } from "./components/RequestForm";
import {
  createMatchRequest,
  type CreateRequestBody,
} from "../../services/leadService";

type Step =
  | "loading"
  | "empty"
  | "creating" // ← was missing
  | "waiting"
  | "lead-review"
  | "lead-pay"
  | "processing"
  | "success"
  | "matched"
  | "error";

export default function Leads() {
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>("loading");
  const [data, setData] = useState<MyRequestPayload | null>(null);
  const [error, setError] = useState("");
  const [payError, setPayError] = useState("");
  const [isActing, setIsActing] = useState(false);
  const [declineReason, setDeclineReason] = useState("");
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [statusMsg, setStatusMsg] = useState("Processing…");
  const [isVerified, setIsVerified] = useState<boolean | null>(null);
  const appDownloadUrl = process.env.REACT_APP_APP_DOWNLOAD_URL || "";

  useEffect(() => {
    if (!localStorage.getItem("accessToken")) {
      navigate("/pay");
      return;
    }
    load();
    loadVerification();
    // eslint-disable-next-line
  }, []);

  async function loadVerification() {
    try {
      const res = await authFetch(
        `${API_BASE_URL}${ENDPOINTS.USERS_ME}`,
        {},
        10000,
      );
      const json = await safeJson(res);
      const u = json?.data ?? json;
      setIsVerified(!!u?.isVerified);
    } catch {
      // Leave as null — don't block the user if the check fails
    }
  }

  function resolveStep(payload: MyRequestPayload): Step {
    const lead: Lead | null = payload.activeLead;
    if (lead) {
      if (lead.status === "MATCHED") return "matched";
      if (lead.status === "AWAITING_PAYMENT") return "lead-pay";
      return "lead-review"; // LEAD
    }
    if (payload.request) return "waiting";
    return "empty";
  }

  async function load() {
    setStep("loading");
    setError("");
    try {
      const payload = await getMyRequest();
      setData(payload);
      setStep(resolveStep(payload));
    } catch (e: any) {
      if (e instanceof ApiError && e.code === "NETWORK") {
        setError("Could not reach the server. Please try again.");
      } else {
        setError(e?.message || "Could not load your leads.");
      }
      setStep("error");
    }
  }

  async function handleCreateRequest(body: CreateRequestBody) {
    setIsActing(true);
    setError("");
    try {
      await createMatchRequest(body);
      await load();
    } catch (e: any) {
      setError(e?.message || "Could not submit request.");
      setStep("creating");
    } finally {
      setIsActing(false);
    }
  }

  // ── Actions ───────────────────────────────────────────────────
  async function handleAccept() {
    if (!data?.activeLead) return;
    setIsActing(true);
    setPayError("");
    try {
      await acceptLead(data.activeLead.id);
      await load();
    } catch (e: any) {
      if (e instanceof ApiError && e.code === "CONFLICT") {
        setPayError("This lead changed underneath you. Refresh and try again.");
        await load();
      } else {
        setPayError(e?.message || "Could not accept the lead.");
      }
    } finally {
      setIsActing(false);
    }
  }

  async function handleDecline() {
    if (!data?.activeLead) return;
    setIsActing(true);
    try {
      await declineLead(data.activeLead.id, declineReason || undefined);
      setShowDeclineModal(false);
      setDeclineReason("");
      await load();
    } catch (e: any) {
      setShowDeclineModal(false);
      setPayError(e?.message || "Could not decline the lead.");
    } finally {
      setIsActing(false);
    }
  }

  async function handleCheckout(body: {
    channel: any;
    msisdn?: string;
    mobileMoneyOperator?: "tnm" | "airtel";
  }) {
    if (!data?.activeLead) return;
    setPayError("");
    setStep("processing");
    setStatusMsg("Starting checkout…");

    try {
      const res = await checkoutLead(data.activeLead.id, body);

      // Card/bank flow: redirect to hosted page
      if (res.requiresRedirect && res.checkoutUrl) {
        window.location.href = res.checkoutUrl;
        return;
      }

      // Mobile money: poll verify
      setStatusMsg("Waiting for payment confirmation…");
      pollVerify(res.reference);
    } catch (e: any) {
      setPayError(e?.message || "Could not start checkout.");
      setStep("lead-pay");
    }
  }

  function pollVerify(reference: string) {
    let attempts = 0;
    const MAX = 45; // 45 × 4s = 3 min
    const iv = setInterval(async () => {
      attempts += 1;
      try {
        const r = await verifyLeadPayment(reference);
        if (r.status === "COMPLETED") {
          clearInterval(iv);
          setStep("success");
        } else if (r.status === "FAILED") {
          clearInterval(iv);
          setPayError("Payment failed or was cancelled.");
          setStep("lead-pay");
        } else if (attempts >= MAX) {
          clearInterval(iv);
          setPayError(
            "Payment timed out. If you completed it, check again shortly.",
          );
          setStep("lead-pay");
        }
      } catch {
        if (attempts >= MAX) {
          clearInterval(iv);
          setPayError("Could not confirm payment status.");
          setStep("lead-pay");
        }
      }
    }, 4000);
  }

  // ── Render

  return (
    <main className="bg-white px-[5%] pt-[140px] pb-20 lg:pb-[100px]">
      <div className="max-w-[900px] mx-auto">
        <Reveal>
          <Eyebrow>My Request</Eyebrow>
        </Reveal>

        {step === "loading" && (
          <div className="text-center py-16">
            <div className="w-10 h-10 border-2 border-black/10 border-t-rose rounded-full animate-spin mx-auto mb-6" />
            <p className="text-[15px] text-muted">Loading your request…</p>
          </div>
        )}

        {step === "error" && (
          <Reveal>
            <div className="mt-6 p-6 rounded-2xl border border-rose/20 bg-blush text-center">
              <p className="text-[14px] text-rose-dark mb-4">{error}</p>
              <button
                type="button"
                onClick={load}
                className="text-sm font-semibold text-black border border-black/10 px-5 py-2.5 rounded-full hover:bg-neutral-50 transition-colors"
              >
                Try again
              </button>
            </div>
          </Reveal>
        )}

        {step === "empty" && (
          <Reveal delay={100}>
            <h1 className="text-3xl font-bold tracking-tight mb-3 mt-4">
              Start a curated request
            </h1>
            <p className="text-[15px] text-muted leading-relaxed mb-8">
              Tell us who you're looking for. A matchmaker will introduce
              candidates to you one at a time.
            </p>
            <button
              type="button"
              onClick={() => setStep("creating")}
              className="inline-block bg-magenta text-white text-sm font-semibold px-6 py-3 rounded-full hover:opacity-90 transition-opacity"
            >
              Create request
            </button>
          </Reveal>
        )}

        {step === "creating" && (
          <Reveal delay={100}>
            <button
              type="button"
              onClick={() => {
                setStep("empty");
                setError("");
              }}
              className="text-sm text-muted hover:text-black mb-4 flex items-center gap-1"
            >
              ← Back
            </button>
            <h1 className="text-3xl font-bold tracking-tight mb-3">
              Your preferences
            </h1>
            <p className="text-[15px] text-muted leading-relaxed mb-8">
              Everything is optional — you can always update these later.
            </p>
            <RequestForm
              onSubmit={handleCreateRequest}
              submitting={isActing}
              error={error}
              isVerified={isVerified}
              appDownloadUrl={appDownloadUrl}
            />
          </Reveal>
        )}

        {step === "waiting" && data?.request && (
          <Reveal delay={100}>
            <h1 className="text-3xl font-bold tracking-tight mb-3 mt-4">
              We're on it
            </h1>
            <p className="text-[15px] text-muted leading-relaxed mb-6">
              Your request is open. A matchmaker will introduce you to a
              candidate soon — we'll notify you in the app.
            </p>

            <div className="p-6 rounded-2xl border border-black/10 bg-white grid grid-cols-2 gap-4">
              <div>
                <p className="text-[11px] uppercase tracking-wider text-muted font-semibold mb-1">
                  Leads used
                </p>
                <p className="text-lg font-bold text-black">
                  {data.leadsUsedThisMonth} / {data.leadCapacity}
                </p>
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-wider text-muted font-semibold mb-1">
                  Resets on
                </p>
                <p className="text-lg font-bold text-black">
                  {new Date(data.capacityResetsAt).toLocaleDateString()}
                </p>
              </div>
              {data.request.expiresAt && (
                <div className="col-span-2">
                  <p className="text-[11px] uppercase tracking-wider text-muted font-semibold mb-1">
                    Request expires
                  </p>
                  <p className="text-[14px] text-black">
                    {new Date(data.request.expiresAt).toLocaleDateString()}
                  </p>
                </div>
              )}
            </div>
          </Reveal>
        )}

        {(step === "lead-review" || step === "lead-pay") &&
          data?.activeLead && (
            <Reveal delay={100}>
              <h1 className="text-3xl font-bold tracking-tight mb-3 mt-4">
                {step === "lead-review"
                  ? "We found someone"
                  : "Complete your match"}
              </h1>
              <p className="text-[15px] text-muted leading-relaxed mb-8">
                {step === "lead-review"
                  ? data.activeLead.respondBy
                    ? `Please accept or decline by ${new Date(
                        data.activeLead.respondBy,
                      ).toLocaleString()}.`
                    : "Please accept or decline this introduction."
                  : data.activeLead.paymentDueAt
                    ? `Complete payment by ${new Date(
                        data.activeLead.paymentDueAt,
                      ).toLocaleString()} to unlock your match.`
                    : "Complete payment to unlock your match."}
              </p>

              <LeadHero lead={data.activeLead} />

              {payError && (
                <p className="mt-5 text-[13px] text-rose-dark">{payError}</p>
              )}

              {step === "lead-review" && (
                <div className="mt-6 flex flex-col sm:flex-row gap-3">
                  <button
                    type="button"
                    onClick={() => setShowDeclineModal(true)}
                    disabled={isActing}
                    className="flex-1 text-sm font-semibold text-black border border-black/10 py-3.5 rounded-full hover:bg-neutral-50 transition-colors disabled:opacity-40"
                  >
                    Decline
                  </button>
                  <button
                    type="button"
                    onClick={handleAccept}
                    disabled={isActing}
                    className="flex-1 bg-magenta text-white text-sm font-semibold py-3.5 rounded-full hover:opacity-90 transition-opacity disabled:opacity-40"
                  >
                    {isActing ? "Accepting…" : "Accept & pay"}
                  </button>
                </div>
              )}

              {step === "lead-pay" && (
                <div className="mt-8 p-6 rounded-2xl border border-black/10 bg-white">
                  <h3 className="text-lg font-bold tracking-tight mb-5">
                    Complete payment
                  </h3>
                  <PaymentPicker
                    amount={data.activeLead.price}
                    currency={data.activeLead.currency}
                    onSubmit={handleCheckout}
                    submitting={false}
                    error={payError}
                  />
                </div>
              )}
            </Reveal>
          )}

        {step === "processing" && (
          <div className="text-center py-16">
            <div className="w-10 h-10 border-2 border-black/10 border-t-rose rounded-full animate-spin mx-auto mb-6" />
            <p className="text-[15px] text-muted">{statusMsg}</p>
          </div>
        )}

        {step === "success" && (
          <Reveal>
            <div className="text-center py-10">
              <div className="w-14 h-14 rounded-full bg-rose/10 flex items-center justify-center mx-auto mb-6">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#c45a7e"
                  strokeWidth="2"
                  className="w-6 h-6"
                >
                  <polyline points="20,6 9,17 4,12" />
                </svg>
              </div>
              <h1 className="text-2xl font-bold tracking-tight mb-3">
                Match confirmed
              </h1>
              <p className="text-[15px] text-muted leading-relaxed mb-8">
                Your payment went through. Open the app to start chatting —
                we'll see you there!
              </p>
              <Link
                to="/account"
                className="inline-block bg-black text-white text-sm font-semibold px-6 py-3 rounded-full hover:bg-neutral-800 transition-colors no-underline"
              >
                Back to account
              </Link>
            </div>
          </Reveal>
        )}

        {step === "matched" && (
          <Reveal>
            <div className="text-center py-10">
              <h1 className="text-2xl font-bold tracking-tight mb-3">
                Already matched
              </h1>
              <p className="text-[15px] text-muted leading-relaxed mb-8">
                Your curated match is live in the app.
              </p>
              <Link
                to="/account"
                className="inline-block bg-black text-white text-sm font-semibold px-6 py-3 rounded-full hover:bg-neutral-800 transition-colors no-underline"
              >
                Back to account
              </Link>
            </div>
          </Reveal>
        )}

        {/* Decline modal */}
        {showDeclineModal && (
          <div
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40"
            onClick={() => !isActing && setShowDeclineModal(false)}
          >
            <div
              className="bg-white rounded-2xl max-w-[440px] w-full p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-bold tracking-tight mb-2">
                Decline this lead?
              </h3>
              <p className="text-[14px] text-muted leading-relaxed mb-5">
                This counts toward your monthly leads and cannot be undone. Tell
                us why (optional):
              </p>
              <textarea
                value={declineReason}
                onChange={(e) => setDeclineReason(e.target.value)}
                rows={3}
                placeholder="Not a good fit, wrong age range, etc."
                className="w-full px-4 py-3 rounded-2xl border border-black/10 text-sm outline-none focus:border-rose transition-colors mb-4 resize-none"
              />
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowDeclineModal(false)}
                  disabled={isActing}
                  className="flex-1 text-[13px] font-semibold text-black border border-black/10 py-3 rounded-full hover:bg-neutral-50 transition-colors disabled:opacity-40"
                >
                  Keep lead
                </button>
                <button
                  type="button"
                  onClick={handleDecline}
                  disabled={isActing}
                  className="flex-1 text-[13px] font-semibold text-white bg-rose-dark py-3 rounded-full hover:opacity-90 transition-opacity disabled:opacity-40"
                >
                  {isActing ? "Declining…" : "Yes, decline"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
